import { ApolloProvider, ApolloClient, InMemoryCache, createHttpLink, defaultDataIdFromObject, HttpLink } from '@apollo/client';
import { ApolloLink } from 'apollo-link';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { useAuth0 } from './auth/useAuth0';
import { GRAPHQL_URL } from './helpers/environment';
import { getToken } from './services/auth';
import { CONSTANTS } from './constants/constants';
import { extractPartFromPathname } from './utils/utils';
import { ORG_REDIRECT_ATTEMPTED_KEY } from './App';

const AUTH_RECOVERY_ATTEMPTED_KEY = 'auth0_auth_recovery_attempted';
// Minimum interval between automatic auth-recovery redirects. Prevents redirect
// loops when 401s are coming from a persistent cause (revoked permissions,
// backend bug) rather than a recoverable expired-session condition.
const AUTH_RECOVERY_DEBOUNCE_MS = 60 * 1000;

export let client;

const ApolloProviderWithAuth0 = ({ children }) => {
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();

  const authLink = setContext(async (_, { headers, ...rest }) => {
    let token;
    try {
      token = await getAccessTokenSilently();
    } catch (error) {
      console.log(error);
    }

    if (!token) {
      token = getToken();
    }

    return {
      ...rest,
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : ''
      }
    };
  });

  // Catches backend 401s that indicate the user's session/refresh token is no
  // longer valid (RT expired past the inactivity/absolute lifetime, rotation
  // race, admin revocation, tenant config change, etc.). Recovers by bouncing
  // through Auth0 to mint a fresh session, then returning the user to the app.
  //
  // Multiple guards prevent this from interfering with other flows:
  // - URL contains code=/error=: an Auth0 redirect callback is currently being
  //   processed by the SDK; bailing out lets that complete normally.
  // - ORG_REDIRECT_ATTEMPTED_KEY set: an org-switch redirect (from
  //   NROrgSelector or CheckAuthentication) is in flight; do not clobber it.
  // - Recent recovery within AUTH_RECOVERY_DEBOUNCE_MS: a previous redirect
  //   attempt happened; bail out to avoid looping on persistent 401s.
  const errorLink = onError(({ networkError }) => {
    const statusCode = networkError?.statusCode ?? networkError?.response?.status;
    if (statusCode !== 401) {
      return;
    }

    const search = typeof window !== 'undefined' ? window.location.search : '';
    if (search.includes('code=') || search.includes('error=')) {
      return;
    }

    if (sessionStorage.getItem(ORG_REDIRECT_ATTEMPTED_KEY)) {
      return;
    }

    const lastAttempt = Number(sessionStorage.getItem(AUTH_RECOVERY_ATTEMPTED_KEY) || 0);
    if (Date.now() - lastAttempt < AUTH_RECOVERY_DEBOUNCE_MS) {
      return;
    }

    sessionStorage.setItem(AUTH_RECOVERY_ATTEMPTED_KEY, String(Date.now()));
    console.warn('[Auth] 401 received from backend, redirecting to Auth0 to re-authenticate');
    loginWithRedirect();
  });

  const cache = new InMemoryCache({
    possibleTypes: {
      ReachabilityEntry: ['SystemdEntry', 'CronEntry', 'ScriptCommandEntry']
    },
    typePolicies: {
      IssueDefinition: {
        // needed because the issue definition key can be repeated
        keyFields: ['id', 'name', 'description']
      },
      Credential: {
        keyFields: ['id', 'user_id', 'shell', 'hash']
      },
      Analytics: {
        // Analytics type doesn't have an ID, so we need field policies for argument-dependent fields
        fields: {
          vulnerabilitiesFunnelChart: {
            // Use keyArgs to tell Apollo to cache separately based on arguments
            keyArgs: ['args'],
            merge(existing, incoming) {
              // For argument-dependent fields, return incoming to replace existing
              return incoming;
            }
          }
        }
      },
      Query: {
        fields: {
          analytics: {
            // Merge function for analytics field to handle argument-dependent fields
            // Since analytics contains fields with arguments (like vulnerabilitiesFunnelChart),
            // and these are cached separately via keyArgs, we merge by combining objects
            // This allows different queries with different arguments to coexist
            merge(existing = {}, incoming) {
              // Since nested fields with keyArgs (like vulnerabilitiesFunnelChart) are
              // cached separately based on their arguments, we can safely merge the
              // top-level Analytics objects. Apollo will resolve the correct nested
              // field values based on the query arguments.
              return { ...existing, ...incoming };
            }
          }
        }
      }
    },
    dataIdFromObject(responseObject) {
      let memoizedAssetId = null;
      const getAssetId = () => {
        if (!memoizedAssetId) {
          memoizedAssetId = responseObject?.assetId || extractPartFromPathname();
        }
        return memoizedAssetId;
      };

      switch (responseObject.__typename) {
        case 'Dependency':
        case 'DenormalizedComponent':
        case 'IssueInstance':
        case 'StringVersion':
        case 'ComponentNeighbor':
          return;
        case 'Asset':
          return responseObject.__typename + `:${responseObject?.id}`;
        case 'AssetEdge':
          return responseObject.__typename + `:${responseObject.node.__ref}`;
        case 'AssetsConnection': {
          // Total-count-only queries omit `edges`; avoid `.at` on undefined.
          const edges = responseObject.edges;
          if (!edges?.length) {
            return;
          }
          return responseObject.__typename + `:${edges.at(0)}to${edges.at(-1)}`;
        }
        case 'AssetsResponse': {
          const assets = responseObject?.assets;
          if (!assets?.length) {
            return;
          }
          return responseObject.__typename + `:${assets.at(0)?.id}to${assets.at(-1)?.id}`;
        }
        case 'AssetVulnerabilityRemediation':
          return responseObject.__typename + `:${responseObject?.vulnerabilityId}-${responseObject?.componentId}`;
        case 'LicenseIssueNode':
          return responseObject.__typename + `:${responseObject?.issueId}`;
        case 'SecretRemediation':
          return responseObject.__typename + `:${responseObject?.secretId}`;
        case 'DetectedVulnerability':
          return responseObject.__typename + `:${getAssetId()}:${responseObject?.cve}-${responseObject?.identificationIds?.join('|')}`;
        case 'CertificateNode':
          return responseObject.__typename + `:${getAssetId()}-${responseObject?.filePath}-${responseObject?.sha256}`;
        case 'AssetCertificateRemediation':
          return responseObject.__typename + `:${getAssetId()}-${responseObject?.certificate?.filePath}-${responseObject?.certificate?.sha256}`;
        case 'PrivateKeyNode':
          return responseObject.__typename + `:${getAssetId()}-${responseObject?.filePath}-${responseObject?.matchHash}`;
        case 'AssetPrivateKeyRemediation':
          return responseObject.__typename + `:${getAssetId()}-${responseObject?.privateKey?.filePath}-${responseObject?.privateKey?.matchHash}`;
        default:
          return defaultDataIdFromObject(responseObject);
      }
    }
  });

  const httpLink1 = new HttpLink({ uri: `http://localhost:4000/graphql/v3` });

  const dynamicLink = new ApolloLink((operation, forward) => {
    // Get the value from the operation context to determine the appropriate URI
    const version = operation.getContext().version || 'v3';
    let uri = GRAPHQL_URL + version;

    // Set the URI on the operation
    operation.setContext({ uri });

    // Forward the operation to the next link
    return forward(operation);
  });

  client = new ApolloClient({
    connectToDevTools: true,
    link: errorLink.concat(authLink).concat(dynamicLink).concat(httpLink1),
    name: user?.org_id || 'netrise',
    version: CONSTANTS.APP_VERSION || '1.0',
    cache,
    defaultOptions: {
      mutate: { errorPolicy: 'none' }
    }
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloProviderWithAuth0;
